import { ChangeDetectionStrategy, Component, forwardRef, Input, OnInit, ViewChild } from '@angular/core';
import { ValidatorService } from '../../core/services/validator.service';
import { AddressSuggestion, AddressSuggestionsService } from '../../core/api';
import { AutocompleteComponent, AutocompleteDataSource, FlyoutOption } from 'visana-ui-library';
import { Address } from '../../pages/key-data/key-data.types';
import { ControlValueAccessor, FormControl, NG_VALUE_ACCESSOR, Validators } from '@angular/forms';
import { map } from 'rxjs/operators';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';

@UntilDestroy()
@Component({
  standalone: false,
  selector: 'ssc-zip-code-autocomplete',
  templateUrl: './zip-code-autocomplete.component.html',
  styleUrls: ['./zip-code-autocomplete.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => ZipCodeAutocompleteComponent),
      multi: true,
    },
  ],
})
export class ZipCodeAutocompleteComponent implements OnInit, ControlValueAccessor {
  @Input() public validateForm: boolean;
  @Input() public saleOfferBegin: string;

  public address = new FormControl<FlyoutOption<Address> | null>(null, Validators.required);
  public addressSuggestions$: AutocompleteDataSource<Address>;
  @ViewChild(AutocompleteComponent)
  private autocomplete: AutocompleteComponent<Address>;

  constructor(
    public validatorService: ValidatorService,
    private addressSuggestionsService: AddressSuggestionsService,
  ) {}

  public ngOnInit(): void {
    this.addressSuggestions$ = (searchTerm: string) =>
      this.addressSuggestionsService.autocomplete(searchTerm, this.saleOfferBegin).pipe(
        map((addressSuggestions) => {
          return this.mapToOption(Array.from(addressSuggestions.suggestions));
        }),
      );
  }

  public updateChoosableOptions(options: FlyoutOption<Address>[]): void {
    this.autocomplete.filteredOptions$.next(options);
  }

  public selectedAddressMapper: (option: FlyoutOption<Address>) => string = (option) =>
    option?.label ? option.label : '';

  public registerOnChange(fn: (value: FlyoutOption<Address> | null) => void): void {
    this.address.valueChanges.pipe(untilDestroyed(this)).subscribe(fn);
  }

  private mapToOption(addressSuggestions: AddressSuggestion[]): FlyoutOption<Address>[] {
    return addressSuggestions.map((address: AddressSuggestion) => ({
      additionalText: address.municipality || undefined,
      value: {
        bfsNumber: address.bfs_number,
        zipCode: address.zip_code,
        city: address.city,
      },
      label: `${address.zip_code} ${address.city}`,
    }));
  }

  public registerOnTouched(): void {}

  public writeValue(value: FlyoutOption<Address> | null): void {
    this.address.setValue(value);
  }
}
